import ExcelJS, { type Workbook } from 'exceljs';

import type { DatasetColumn } from '../types/reporting';
import type { ResultRow } from './arrowToRows';
import {
  type DimensionFormat,
  formatValue,
  humanizeHeader,
  resolveFormatKind,
} from './formatValue';

// --- Design decisions (also recorded in PR notes) ---
// (1) Reuse the existing exceljs@^4.4.0 dependency, NOT DuckDB, to write .xlsx.
//     DuckDB-WASM has no reliable in-browser xlsx writer, and exceljs already
//     ships a canonical client-side pattern in utils/adoptionReportExport.ts
//     (Workbook → addWorksheet → addRow → xlsx.writeBuffer → Blob → anchor).
// (2) Money/number cells whose raw value is a real number are written as REAL
//     numeric cells with an Excel numFmt (not the formatted strings the CSV
//     path emits), so Excel can sum/sort/aggregate them natively. This also
//     SIMPLIFIES the formula-injection story: the tab-guard only ever applies
//     to string-source cells, so a machine-formatted number can never be
//     mangled and can never carry a formula payload. Date/text/bucket cells
//     stay formatted strings to match the table exactly (date buckets like
//     "Q2 2026" aren't real dates anyway).

// A leading =, +, -, @ (or a leading tab/CR a spreadsheet trims to expose one)
// makes Excel/Sheets evaluate the cell as a formula on open — an injection
// vector. Copied verbatim from exportCsv.ts (the shared rationale lives there):
// neutralize by prefixing a tab so the value stays readable but is forced to
// text. Applied ONLY to string-source cells (see buildWorkbook), never to
// machine-formatted numbers, so a legitimate negative like -12.34 stays a real
// number and keeps Excel's numeric sort/aggregation working.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

// Excel number formats approximating the Intl currency/decimal formatters in
// formatValue.ts. Money → USD with cents; integers → grouped no-decimals;
// non-integer decimals → grouped two-decimals.
const MONEY_FMT = '$#,##0.00';
const INT_FMT = '#,##0';
const DEC_FMT = '#,##0.00';

const SHEET_NAME = 'Report';

const neutralize = (text: string): string =>
  FORMULA_TRIGGER.test(text) ? `\t${text}` : text;

/**
 * Build an in-memory ExcelJS workbook for a reporting result. A bold humanized
 * header row plus one row per result row, using the same domain formatting the
 * table and CSV export use (resolveFormatKind + formatValue + humanizeHeader),
 * so headers and values read identically everywhere. Money/number cells with a
 * numeric raw value are written as real numbers with an Excel numFmt; every
 * other cell is the same formatted string the table shows. Synchronous: pure
 * construction, no I/O — only serialization (downloadXlsx) is async.
 */
export function buildWorkbook(
  columns: string[],
  rows: ResultRow[],
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>,
  dimFormats?: Map<string, DimensionFormat>,
  pivotColumn?: string
): Workbook {
  // Identical header/value resolution to toCsv so the export matches the table.
  const pivot = pivotColumn ? { column: pivotColumn } : undefined;
  const dims = columns.map(c => dimFormats?.get(c));
  const kinds = columns.map((c, i) =>
    resolveFormatKind(c, columnMeta, dims[i])
  );

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(SHEET_NAME);

  // Headers are formula-guarded like the CSV path: a pivot value column's name
  // is derived from row data, so a header can carry an injection payload too.
  const headerRow = worksheet.addRow(
    columns.map(c =>
      neutralize(humanizeHeader(c, columnLabels, columnMeta, pivot))
    )
  );
  headerRow.font = { bold: true };

  for (const row of rows) {
    const excelRow = worksheet.addRow([]);
    columns.forEach((c, i) => {
      const cell = excelRow.getCell(i + 1);
      const raw = row[c];
      if (raw === null || raw === undefined || raw === '') {
        // Leave blank (matches the CSV empty field, not the em-dash placeholder).
        return;
      }
      const kind = kinds[i];
      if (
        (kind === 'money' || kind === 'number') &&
        typeof raw === 'number' &&
        Number.isFinite(raw)
      ) {
        // Real numeric cell so Excel can sum/sort/aggregate. Numbers can't carry
        // a formula payload, so no tab-guard is needed here. NaN/±Infinity are
        // excluded (they'd write an invalid Excel numeric cell) and fall through
        // to the safe formatted-string path below.
        cell.value = raw;
        cell.numFmt =
          kind === 'money'
            ? MONEY_FMT
            : Number.isInteger(raw)
              ? INT_FMT
              : DEC_FMT;
        return;
      }
      const text = formatValue(raw, kind, {
        dateGranularity: dims[i]?.dateGranularity,
        emptyText: '',
      });
      // Only user-controlled STRING source values can carry a formula payload;
      // numbers/dates are formatted by us and safe (mirrors exportCsv's guard
      // boundary so CSV/XLSX neutralization behavior stays identical).
      cell.value = typeof raw === 'string' ? neutralize(text) : text;
    });
  }

  return workbook;
}

/**
 * Serialize a workbook to .xlsx and trigger a browser download. Mirrors the
 * exceljs download pattern in utils/adoptionReportExport.ts and downloadCsv.
 */
export async function downloadXlsx(
  filename: string,
  workbook: Workbook
): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Convenience wrapper matching toCsv's parameter list so the call site swaps
 * `downloadCsv(name, toCsv(...))` for `await exportRowsToXlsx(name, ...)`.
 */
export async function exportRowsToXlsx(
  filename: string,
  columns: string[],
  rows: ResultRow[],
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>,
  dimFormats?: Map<string, DimensionFormat>,
  pivotColumn?: string
): Promise<void> {
  await downloadXlsx(
    filename,
    buildWorkbook(
      columns,
      rows,
      columnLabels,
      columnMeta,
      dimFormats,
      pivotColumn
    )
  );
}
